import { ChangeEvent, ReactElement, useCallback, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useLatest } from 'react-use'
import { Vector3 } from 'three'
import { Close } from '@mui/icons-material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Button, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import usePlanImageCardMutations from '@editor/hooks/usePlanImageCardMutations'
import PlanExportConfigFields from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanExportConfigFields'
import { Html } from '@react-three/drei'
import { useCanvasEvent } from '@editorHooks'
import { DEFAULT_PLAN_IMAGE_CARD_WIDTH_CM } from 'src/domain/planImageCards/defaults'
import type { PlanImageCard } from 'src/domain/planImageCards/types'

interface Props {
  planImageCard: PlanImageCard
  /** World-space anchor (the card's top-right corner) the popup floats next to. */
  anchor: Vector3
  onClose: () => void
}

/**
 * In-scene quick editor for a plan image card: name, title lines, width and the
 * plan/storey export config, plus an image upload. Mirrors the comment edit popup
 * (a drei `<Html>` panel that closes on a canvas click or Escape). Field edits are
 * held locally and persisted once when the popup closes — matching the comment
 * popup and avoiding a backend save per keystroke; the image upload is a separate
 * immediate action (it replaces the asset), same as the sidebar form.
 */
const PlanImageCardEditPopup = ({ planImageCard, anchor, onClose }: Props): ReactElement => {
  // The drei <Html> portal renders outside the r3f tree, so the app's MUI theme
  // context doesn't reach it — re-provide it from the theme available here.
  const theme = useTheme()
  const {
    updatePlanImageCardFields,
    flushPlanImageCardPersist,
    replacePlanImageCardAsset,
    isUploadingAsset,
    isReplacingImageAsset,
  } = usePlanImageCardMutations()

  // Pending edits, applied on close. Read via a ref so the close handler (and its
  // canvas/keydown listeners) stays stable while typing.
  const [draft, setDraft] = useState<Partial<PlanImageCard>>({})
  const draftRef = useLatest(draft)
  const planConfig = draft.planConfig ?? planImageCard.planConfig

  // Imperative click into a hidden file input (mirrors the sidebar form) — the
  // upload replaces the card's image asset immediately.
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return
      await replacePlanImageCardAsset(planImageCard.id, file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [planImageCard.id, replacePlanImageCardAsset],
  )

  const handleClose = useCallback(() => {
    const changes = draftRef.current
    if (Object.keys(changes).length > 0) {
      updatePlanImageCardFields(planImageCard.id, changes)
      flushPlanImageCardPersist()
    }
    onClose()
  }, [draftRef, planImageCard.id, updatePlanImageCardFields, flushPlanImageCardPersist, onClose])

  // A left-button pointer-down on the scene canvas persists and closes the popup;
  // non-left buttons are ignored so a right-button camera drag keeps it open (see
  // PlanTableEditPopup).
  const handleCanvasPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.button !== 0) return
      handleClose()
    },
    [handleClose],
  )
  useCanvasEvent('pointerdown', handleCanvasPointerDown)

  useHotkeys(
    'esc',
    event => {
      event.stopPropagation()
      handleClose()
    },
    { enableOnFormTags: true },
    [handleClose],
  )

  return (
    <Html position={anchor} zIndexRange={[100, 0]} style={{ pointerEvents: 'auto' }}>
      <ThemeProvider theme={theme}>
        <Paper
          elevation={4}
          sx={{ p: 1.5, width: 280, transform: 'translate(16px, 0)' }}
          onPointerDown={event => event.stopPropagation()}
          data-cy="plan-image-card-edit-popup"
        >
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Plan-Bild bearbeiten</Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ mr: -0.5, mt: -0.5 }}
                data-cy="plan-image-card-popup-close"
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>

            <TextField
              label="Name"
              size="small"
              // Uncontrolled: a controlled value would be re-applied by drei's frequent <Html>
              // re-renders, resetting the caret while typing.
              defaultValue={planImageCard.name}
              onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
              fullWidth
              data-cy="plan-image-card-popup-name-input"
            />

            <TextField
              label="Titelzeilen"
              size="small"
              defaultValue={planImageCard.titleLines.join('\n')}
              onChange={event =>
                setDraft(prev => ({ ...prev, titleLines: event.target.value.split('\n') }))
              }
              multiline
              minRows={2}
              fullWidth
              helperText="Eine Zeile pro Titelzeile"
              data-cy="plan-image-card-popup-title-input"
            />

            <TextField
              label="Breite (PDF cm)"
              size="small"
              type="number"
              defaultValue={planImageCard.width}
              onChange={event => {
                const width = Number(event.target.value)
                if (!Number.isFinite(width) || width <= 0) return
                setDraft(prev => ({ ...prev, width }))
              }}
              inputProps={{ min: 1, step: 1 }}
              fullWidth
              helperText={`Standard: ${DEFAULT_PLAN_IMAGE_CARD_WIDTH_CM} cm`}
              data-cy="plan-image-card-popup-width-input"
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleUploadClick}
                disabled={isUploadingAsset || isReplacingImageAsset}
                data-cy="plan-image-card-popup-upload-button"
              >
                Bild hochladen
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                hidden
                onChange={handleFileChange}
                data-cy="plan-image-card-popup-upload-input"
              />
            </Stack>

            <PlanExportConfigFields
              planConfig={planConfig}
              onChange={nextConfig => setDraft(prev => ({ ...prev, planConfig: nextConfig }))}
            />
          </Stack>
        </Paper>
      </ThemeProvider>
    </Html>
  )
}

export default PlanImageCardEditPopup
